import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const { FiCheck, FiMoreHorizontal, FiPlus } = FiIcons;

const INITIAL_TASKS = [
  { id: 1, title: 'Sign permission slip for Leo', category: 'School', assignee: 'Mom', completed: false },
  { id: 2, title: 'Buy snacks for soccer team', category: 'Shopping', assignee: 'Dad', completed: false },
  { id: 3, title: 'Pay daycare invoice', category: 'Finance', assignee: 'Mom', completed: true },
  { id: 4, title: 'Wash soccer uniforms', category: 'Chores', assignee: 'Anyone', completed: false },
];

export default function TasksView() {
  const [tasks, setTasks] = useState(INITIAL_TASKS);
  const [activeTab, setActiveTab] = useState('all');

  const toggleTask = (id) => {
    setTasks(tasks.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
  };

  const filteredTasks = tasks.filter(t => {
    if (activeTab === 'active') return !t.completed;
    if (activeTab === 'completed') return t.completed;
    return true;
  });

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-[#1a365d]">Shared Checklists</h2>
        <button className="bg-yellow-300 text-[#1a365d] px-4 py-2 rounded-xl font-bold flex items-center gap-2 hover:bg-yellow-400 transition-colors shadow-sm">
          <SafeIcon icon={FiPlus} />
          New List
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-slate-200/50 p-1 rounded-xl w-fit">
        {['all', 'active', 'completed'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${
              activeTab === tab ? 'bg-white text-[#1a365d] shadow-sm' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Task List */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
          <h3 className="font-bold text-slate-700">Family Inbox</h3>
          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded-full font-medium">
            {tasks.filter(t => !t.completed).length} remaining
          </span>
        </div>
        
        <div className="divide-y divide-slate-100">
          <AnimatePresence>
            {filteredTasks.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="p-8 text-center text-slate-400"
              >
                No tasks here! 🎉
              </motion.div>
            ) : (
              filteredTasks.map(task => (
                <motion.div
                  key={task.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={`p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors group ${
                    task.completed ? 'opacity-60' : ''
                  }`}
                >
                  <button 
                    onClick={() => toggleTask(task.id)}
                    className={`w-6 h-6 rounded-md flex items-center justify-center border-2 transition-colors flex-shrink-0 ${
                      task.completed ? 'bg-green-400 border-green-400 text-white' : 'border-slate-300 hover:border-[#1a365d]'
                    }`}
                  >
                    {task.completed && <SafeIcon icon={FiCheck} className="text-sm" />}
                  </button>
                  
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate transition-all ${
                      task.completed ? 'text-slate-400 line-through' : 'text-slate-800'
                    }`}>
                      {task.title}
                    </p>
                    <div className="flex gap-2 mt-1">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                        {task.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="hidden sm:flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-lg">
                      <div className="w-4 h-4 bg-[#1a365d] rounded-full flex items-center justify-center text-white text-[8px]">
                        {task.assignee.charAt(0)}
                      </div>
                      {task.assignee}
                    </div>
                    <button className="text-slate-400 hover:text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity">
                      <SafeIcon icon={FiMoreHorizontal} />
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}